import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowLeft, Save, Send, CheckCircle2, XCircle, BookCheck,
  RotateCcw, Ban, Plus, Trash2, AlertTriangle, CheckCircle,
  FileText, History, Paperclip, ShieldCheck, Search, X, Printer,
} from 'lucide-react';

import { glJournalApi }          from '@/api/glJournal.api';
import { accountApi }            from '@/api/account.api';
import { transactionTypeApi }    from '@/api/transactionType.api';
import { currencyApi }           from '@/api/currency.api';
import { exchangeRateApi }       from '@/api/exchangeRate.api';
import { usePermission }         from '@/hooks/usePermission';
import { Permissions }           from '@/constants/permissions';
import { AppRoutes }             from '@/constants/routes';
import { Spinner }               from '@/components/ui/Spinner';
import { MasterSearchModal }     from '@/components/master/MasterSearchModal';
import { AccountLedgerButton, ledgerShortcut } from '@/components/erp/AccountLedgerButton';
import { ERPContextBar }          from '@/components/erp/ERPContextBar';
import { WorkflowStatusPanel }   from '@/components/erp/WorkflowStatusPanel';
import { ApprovalTimeline }      from '@/components/erp/ApprovalTimeline';
import { AttachmentPanel }       from '@/components/erp/AttachmentPanel';
import { AuditViewer }           from '@/components/erp/AuditViewer';
import { MasterReportPreview }   from '@/components/master/report/MasterReportPreview';
import { GlJournalVoucher }      from '@/components/gl/GlJournalVoucher';
import { exportToExcel }         from '@/utils/reportExport';
import { setErpContext, clearErpContext } from '@/state/erpContextSlice';
import { cn }                    from '@/utils/cn';

import type { RootState }               from '@/state/store';
import { costCentresForLocation } from '@/utils/costCentreScope';
import type { SearchColumn }            from '@/components/master/MasterSearchModal';
import type { AccountListDto }          from '@/types/account.types';
import type { TransactionTypeListDto }  from '@/types/transactionType.types';
import type {
  GlJournalDto,
  GlJournalLineRequest,
  CreateGlJournalRequest,
  UpdateGlJournalRequest,
  WorkflowStatus,
} from '@/types/glJournal.types';
import { WORKFLOW_STATUS_LABELS, WORKFLOW_STATUS_COLORS } from '@/types/glJournal.types';

// ── Account search columns ─────────────────────────────────────────────────────

const ACCOUNT_COLUMNS: SearchColumn<AccountListDto>[] = [
  { key: 'code',  label: 'Code',  className: 'w-28 font-mono text-blue-700' },
  { key: 'name',  label: 'Account Name' },
  { key: 'accountType', label: 'Type', className: 'w-24',
    render: r => (
      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-gray-300">
        {r.accountType}
      </span>
    )},
];

// ── Line row type ──────────────────────────────────────────────────────────────

interface LineRow {
  key:          string;
  lineNumber:   number;
  accountId:    string;
  accountCode:  string;
  accountName:  string;
  costCenterId: string;
  locationId:   string;
  currencyCode: string;
  exchangeRate: number;
  debitAmount:  number;
  creditAmount: number;
  narration:    string;
  reference:    string;
}

const mkLine = (n: number, ccy: string): LineRow => ({
  key:          `new-${Date.now()}-${n}-${Math.random()}`,
  lineNumber:   n,
  accountId:    '',
  accountCode:  '',
  accountName:  '',
  costCenterId: '',
  locationId:   '',
  currencyCode: ccy,
  exchangeRate: 1,
  debitAmount:  0,
  creditAmount: 0,
  narration:    '',
  reference:    '',
});

// ── Inline account cell ────────────────────────────────────────────────────────

interface AccountCellProps {
  accountId:   string;
  accountCode: string;
  accountName: string;
  disabled:    boolean;
  /**
   * The location this line will post at (GAP-003). Accounts restricted to other locations are not
   * offered. Advisory only — the server refuses one on save regardless of what the picker showed.
   */
  locationId:  string | null;
  onChange:    (id: string, code: string, name: string) => void;
}

const AccountCell = ({
  accountId, accountCode, accountName, disabled, locationId, onChange,
}: AccountCellProps) => {
  const [open, setOpen] = useState(false);

  const fetchAccounts = async (search: string, page: number, pageSize: number) => {
    const res = await accountApi.getList({
      search, pageNumber: page, pageSize, isPostingAccount: true,
      locationId: locationId || undefined,
    });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  };

  const handleSelect = (item: AccountListDto) => {
    onChange(item.id, item.code, item.name);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('', '', '');
  };

  return (
    <>
      <div
        onClick={() => !disabled && setOpen(true)}
        className={cn(
          'flex items-center gap-1.5 h-8 px-2 rounded text-sm border min-w-[180px]',
          disabled
            ? 'bg-transparent border-transparent cursor-default'
            : 'border-transparent hover:border-slate-200 hover:bg-white cursor-pointer focus:border-blue-400',
          accountId ? '' : 'text-slate-400',
        )}
        title={accountId ? `${accountCode} — ${accountName}` : 'Click to select account'}
      >
        {accountId ? (
          <>
            <span className="font-mono text-xs font-semibold text-blue-700 dark:text-blue-400 flex-shrink-0">
              {accountCode}
            </span>
            <span className="text-slate-600 dark:text-slate-300 truncate text-xs flex-1">
              {accountName}
            </span>
            <AccountLedgerButton accountId={accountId} />
            {!disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="flex-shrink-0 text-slate-300 hover:text-red-500 transition-colors"
              >
                <X size={11} />
              </button>
            )}
          </>
        ) : (
          <>
            {!disabled && <Search size={11} className="flex-shrink-0 text-slate-300" />}
            <span className="text-xs italic text-slate-400">
              {disabled ? '—' : 'Select account…'}
            </span>
          </>
        )}
      </div>

      <MasterSearchModal<AccountListDto>
        isOpen={open}
        onClose={() => setOpen(false)}
        title="Select Posting Account"
        columns={ACCOUNT_COLUMNS}
        fetchData={fetchAccounts}
        onSelect={handleSelect}
      />
    </>
  );
};

// ── Tabs ───────────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'lines',       label: 'Journal Lines',   icon: FileText    },
  { key: 'workflow',    label: 'Workflow',         icon: History     },
  { key: 'attachments', label: 'Attachments',      icon: Paperclip   },
  { key: 'audit',       label: 'Audit Log',        icon: ShieldCheck },
] as const;
type Tab = (typeof TABS)[number]['key'];

// ── Action Modal ───────────────────────────────────────────────────────────────

interface ActionModalProps {
  title:       string;
  placeholder: string;
  required:    boolean;
  onConfirm:   (text: string) => void;
  onCancel:    () => void;
}

const ActionModal = ({ title, placeholder, required, onConfirm, onCancel }: ActionModalProps) => {
  const [text, setText] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md p-6">
        <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-3">{title}</h3>
        <textarea
          ref={ref}
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={placeholder}
          rows={4}
          className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm
                     bg-white dark:bg-slate-700 text-slate-900 dark:text-white resize-none
                     focus:ring-2 focus:ring-blue-500 outline-none"
        />
        <div className="flex gap-3 mt-4 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600
                       hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-slate-700 dark:text-slate-300"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(text)}
            disabled={required && !text.trim()}
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700
                       disabled:opacity-50 transition-colors"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Field helpers ──────────────────────────────────────────────────────────────

const fieldClass = (disabled: boolean) => cn(
  'w-full px-3 py-2 text-sm border rounded-lg outline-none transition-colors',
  'bg-white dark:bg-slate-700 text-slate-900 dark:text-white',
  'border-slate-200 dark:border-slate-600',
  disabled
    ? 'bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-gray-400 cursor-default'
    : 'focus:ring-2 focus:ring-blue-500 focus:border-transparent',
);

// ── Main Component ─────────────────────────────────────────────────────────────

export const GlJournalFormPage = () => {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isNew    = !id;

  const { hasPermission } = usePermission();
  const canEdit       = hasPermission(Permissions.GL_JOURNAL_EDIT);
  const canSubmit     = hasPermission(Permissions.GL_JOURNAL_SUBMIT);
  const canApprove    = hasPermission(Permissions.GL_JOURNAL_APPROVE);
  const canReject     = hasPermission(Permissions.GL_JOURNAL_REJECT);
  const canPost       = hasPermission(Permissions.GL_JOURNAL_POST);
  const canReverse    = hasPermission(Permissions.GL_JOURNAL_REVERSE);
  const canCancel     = hasPermission(Permissions.GL_JOURNAL_CANCEL);
  const canAddDoc     = hasPermission(Permissions.DOC_ATTACHMENT_ADD);
  const canDeleteDoc  = hasPermission(Permissions.DOC_ATTACHMENT_DELETE);

  // Current user + session from Redux
  const authUser             = useSelector((s: RootState) => s.auth.user);
  const session              = useSelector((s: RootState) => s.session.session);
  const accessibleLocations  = useSelector((s: RootState) => s.auth.accessibleLocations);
  const accessibleCostCenters = useSelector((s: RootState) => s.auth.accessibleCostCenters);

  // ── Reference data ───────────────────────────────────────────────────────────
  const [txTypes,    setTxTypes]    = useState<TransactionTypeListDto[]>([]);
  const [currencies, setCurrencies] = useState<{ code: string; name: string }[]>([]);

  useEffect(() => {
    transactionTypeApi.getList({ sourceModule: 'GL', pageSize: 100 })
      .then(r => setTxTypes(r.data.data?.items ?? []))
      .catch(() => {});
    currencyApi.getList({ pageSize: 100, isActive: true })
      .then(r => setCurrencies(
        (r.data.data?.items ?? []).map((c: { code: string; name: string }) => ({ code: c.code, name: c.name }))
      ))
      .catch(() => {});
  }, []);

  // ── Journal state ────────────────────────────────────────────────────────────
  const [journal,    setJournal]    = useState<GlJournalDto | null>(null);
  const [loading,    setLoading]    = useState(!isNew);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [success,    setSuccess]    = useState<string | null>(null);
  const [tab,        setTab]        = useState<Tab>('lines');
  const [modal,      setModal]      = useState<'approve' | 'reject' | 'reverse' | 'cancel' | null>(null);
  const [isPrintOpen, setIsPrintOpen] = useState(false);

  // ── Header form ──────────────────────────────────────────────────────────────
  const today = new Date().toISOString().split('T')[0];
  const [transactionTypeCode, setTransactionTypeCode] = useState('JV');
  const [documentDate,        setDocumentDate]        = useState(today);
  const [postingDate,         setPostingDate]         = useState(today);
  const [reference,           setReference]           = useState('');
  const [narration,           setNarration]           = useState('');
  // Default currency = company base currency (from auth state, falls back to USD)
  const baseCurrency = authUser?.baseCurrencyCode ?? session?.companyCurrency ?? 'USD';
  const [currencyCode,        setCurrencyCode]        = useState(baseCurrency);
  const [exchangeRate,        setExchangeRate]        = useState(1);

  // ── Header context (Location + Cost Center) ───────────────────────────────
  // Store IDs only; names/codes are derived from the accessible lists.
  const [headerLocationId,   setHeaderLocationId]   = useState('');
  const [headerCostCenterId, setHeaderCostCenterId] = useState('');

  // Derived helpers
  const selectedLocation = accessibleLocations.find(l => l.id === headerLocationId) ?? null;

  // Cost centres belonging to the selected location. An untagged one is not offered (ML-02).
  const filteredCostCenters = costCentresForLocation(accessibleCostCenters, headerLocationId);

  // Seed location/CC from session when creating a new journal.
  // Re-runs when session location changes mid-session (context switch) so the
  // new journal always defaults to the current session context.
  useEffect(() => {
    if (!isNew) return;
    setHeaderLocationId(session?.locationId   ?? '');
    setHeaderCostCenterId(session?.costCenterId ?? '');
  }, [isNew, session?.locationId, session?.costCenterId]);

  // When location changes, drop a cost centre that does not belong to the new one. Checked against
  // what the new location actually offers, so an untagged cost centre is cleared too — it is no
  // longer selectable anywhere, and leaving it on the header would post it (ML-02).
  const handleLocationChange = (locId: string) => {
    setHeaderLocationId(locId);
    const stillOffered = costCentresForLocation(accessibleCostCenters, locId)
      .some(cc => cc.id === headerCostCenterId);
    if (headerCostCenterId && !stillOffered) setHeaderCostCenterId('');
  };

  // ── Auto-fetch exchange rate when currency or date changes ────────────────────
  useEffect(() => {
    // If same as base currency → always 1, no lookup needed
    if (!currencyCode || currencyCode === baseCurrency) {
      if (isNew) setExchangeRate(1);
      return;
    }
    // Fetch most recent approved rate: FROM base → TO selected
    exchangeRateApi.getList({
      fromCurrencyCode: baseCurrency,
      toCurrencyCode:   currencyCode,
      pageSize:         10,
    }).then(res => {
      const items = res.data?.data?.items ?? [];
      // Pick the most recent rate that is on or before the document date
      const docDate = documentDate;
      const sorted = items
        .filter(r => r.effectiveDate <= docDate)
        .sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate));
      if (sorted.length > 0) {
        setExchangeRate(sorted[0].rate);
      } else if (items.length > 0) {
        // Fallback: just use the latest rate even if it's after the doc date
        setExchangeRate(items[0].rate);
      }
    }).catch(() => {/* leave exchange rate unchanged on error */});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyCode, documentDate]);

  // ── Lines ────────────────────────────────────────────────────────────────────
  const [lines, setLines] = useState<LineRow[]>([
    mkLine(1, baseCurrency),
    mkLine(2, baseCurrency),
  ]);

  const totalDebit  = lines.reduce((s, l) => s + (l.debitAmount  || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (l.creditAmount || 0), 0);
  const diff        = Math.abs(totalDebit - totalCredit);
  const isBalanced  = diff < 0.005 && totalDebit > 0;

  // ── Load journal ─────────────────────────────────────────────────────────────
  const loadJournal = useCallback(async () => {
    if (isNew || !id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await glJournalApi.getById(id);
      const j = res.data?.data;
      if (!j) throw new Error('Journal not found');
      setJournal(j);
      setTransactionTypeCode(j.transactionTypeCode);
      setDocumentDate(j.documentDate.split('T')[0]);
      setPostingDate(j.postingDate.split('T')[0]);
      setReference(j.reference ?? '');
      setNarration(j.narration ?? '');
      setCurrencyCode(j.currencyCode);
      setExchangeRate(j.exchangeRate);
      setHeaderLocationId(j.locationId   ?? '');
      setHeaderCostCenterId(j.costCenterId ?? '');
      setLines(j.lines.map(l => ({
        key:          l.id,
        lineNumber:   l.lineNumber,
        accountId:    l.accountId,
        accountCode:  l.accountCode,
        accountName:  l.accountName,
        costCenterId: l.costCenterId ?? '',
        locationId:   l.locationId ?? '',
        currencyCode: l.currencyCode,
        exchangeRate: l.exchangeRate,
        debitAmount:  l.debitAmount,
        creditAmount: l.creditAmount,
        narration:    l.narration ?? '',
        reference:    l.reference ?? '',
      })));

      // ── Dispatch ERP context to Redux for ERPContextBar ──────────────────
      dispatch(setErpContext({
        companyCode:         authUser?.companyId?.substring(0, 8).toUpperCase() ?? 'COMPANY',
        companyName:         authUser?.fullName ? `(${authUser.username})` : '',
        locationCode:        null,
        locationName:        j.locationName ?? null,
        costCenterCode:      null,
        costCenterName:      j.costCenterName ?? null,
        financialYearCode:   j.financialYearCode,
        financialPeriodCode: j.financialPeriodCode,
        transactionTypeCode: j.transactionTypeCode,
        documentNumber:      j.documentNumber,
        currencyCode:        j.currencyCode,
        exchangeRate:        j.exchangeRate,
      }));
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to load journal');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, dispatch, authUser]);

  useEffect(() => { loadJournal(); }, [loadJournal]);

  // Clear ERP context on unmount
  useEffect(() => {
    return () => { dispatch(clearErpContext()); };
  }, [dispatch]);

  // ── Derived state ────────────────────────────────────────────────────────────
  const status: WorkflowStatus = journal?.workflowStatus ?? 'Draft';
  const isDraftEditable = isNew || status === 'Draft' || status === 'Rejected';
  const isEditable      = isDraftEditable && (isNew || canEdit);

  // ── Build payload ────────────────────────────────────────────────────────────
  const buildLines = (): GlJournalLineRequest[] =>
    lines
      .filter(l => l.accountId || l.debitAmount || l.creditAmount)
      .map((l, idx) => ({
        lineNumber:    idx + 1,
        accountId:     l.accountId,
        costCenterId:  l.costCenterId || null,
        locationId:    l.locationId || null,
        currencyCode:  l.currencyCode || currencyCode,
        exchangeRate:  l.exchangeRate || 1,
        foreignDebit:  l.debitAmount,
        foreignCredit: l.creditAmount,
        debitAmount:   l.debitAmount * (l.exchangeRate || 1),
        creditAmount:  l.creditAmount * (l.exchangeRate || 1),
        narration:     l.narration || null,
        reference:     l.reference || null,
      }));

  // ── Save ─────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setError(null);
    const linesPayload = buildLines();
    if (linesPayload.length < 2) {
      setError('At least 2 journal lines are required.');
      return;
    }
    const missingAccounts = linesPayload.filter(l => !l.accountId);
    if (missingAccounts.length > 0) {
      setError('All journal lines must have an account selected.');
      return;
    }
    if (!isBalanced) {
      setError('Journal is not balanced — total debits must equal total credits.');
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        const payload: CreateGlJournalRequest = {
          locationId:   headerLocationId   || null,
          locationCode: selectedLocation?.code ?? session?.locationCode ?? null,
          costCenterId: headerCostCenterId || null,
          transactionTypeCode, documentDate, postingDate,
          reference: reference || null,
          narration: narration || null,
          currencyCode, exchangeRate,
          lines: linesPayload,
        };
        const res = await glJournalApi.create(payload);
        const newId = res.data?.data?.id;
        setSuccess('Journal created successfully.');
        setTimeout(() => navigate(`/gl/journals/${newId}`), 800);
      } else if (journal) {
        const payload: UpdateGlJournalRequest = {
          locationId:   headerLocationId   || null,
          costCenterId: headerCostCenterId || null,
          documentDate, postingDate,
          reference: reference || null,
          narration: narration || null,
          currencyCode, exchangeRate,
          lines: linesPayload,
        };
        await glJournalApi.update(journal.id, payload);
        setSuccess('Journal updated successfully.');
        setTimeout(() => { setSuccess(null); loadJournal(); }, 800);
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message ?? 'Failed to save journal.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  // ── Workflow actions ─────────────────────────────────────────────────────────
  const doAction = async (action: () => Promise<void>, successMsg: string) => {
    setError(null);
    setSaving(true);
    try {
      await action();
      setSuccess(successMsg);
      setTimeout(() => { setSuccess(null); loadJournal(); }, 1200);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message ?? 'Action failed.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit  = () => doAction(async () => { await glJournalApi.submit(journal!.id); },                              'Journal submitted for approval.');
  const handlePost    = () => doAction(async () => { await glJournalApi.post(journal!.id); },                                'Journal posted to the general ledger.');
  const handleApprove = (remarks: string) => doAction(async () => { await glJournalApi.approve(journal!.id, remarks); },    'Journal approved.');
  const handleReject  = (reason: string)  => doAction(async () => { await glJournalApi.reject(journal!.id, reason); },      'Journal rejected.');
  const handleCancel  = (reason: string)  => doAction(async () => { await glJournalApi.cancel(journal!.id, reason); },      'Journal cancelled.');
  const handleReverse = (narr: string)    => doAction(async () => {
    const today2 = new Date().toISOString().split('T')[0];
    const res = await glJournalApi.reverse(journal!.id, today2, narr || undefined);
    const revId = res.data?.data?.id;
    setTimeout(() => navigate(`/gl/journals/${revId}`), 1200);
  }, 'Reversal journal created.');

  // ── Line grid helpers ─────────────────────────────────────────────────────────
  const updateLine = (key: string, patch: Partial<LineRow>) =>
    setLines(ls => ls.map(l => l.key === key ? { ...l, ...patch } : l));

  const addLine = () =>
    setLines(ls => [...ls, mkLine(ls.length + 1, currencyCode)]);

  const removeLine = (key: string) =>
    setLines(ls => ls.filter(l => l.key !== key));

  // ── Loading state ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="lg" />
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900 overflow-hidden">

      {/* ── ERP Context Bar ──────────────────────────────────────────────────── */}
      <ERPContextBar />

      {/* ── Toolbar ──────────────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-2.5">
        <div className="flex items-center gap-2 flex-wrap">

          <button
            onClick={() => navigate(AppRoutes.GL_JOURNALS)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400
                       hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700
                       rounded-lg transition-colors"
          >
            <ArrowLeft size={14} /> Back
          </button>

          <div className="w-px h-6 bg-slate-200 dark:bg-slate-600" />

          {/* Save */}
          {isEditable && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium"
            >
              {saving ? <Spinner size="sm" /> : <Save size={14} />}
              Save Draft
            </button>
          )}

          {/* Submit */}
          {!isNew && status === 'Draft' && canSubmit && (
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-sky-600 hover:bg-sky-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium"
            >
              <Send size={14} /> Submit for Approval
            </button>
          )}

          {/* Approve */}
          {!isNew && (status === 'Submitted' || status === 'PendingApproval') && canApprove && (
            <button
              onClick={() => setModal('approve')}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium"
            >
              <CheckCircle2 size={14} /> Approve
            </button>
          )}

          {/* Reject */}
          {!isNew && (status === 'Submitted' || status === 'PendingApproval') && canReject && (
            <button
              onClick={() => setModal('reject')}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium"
            >
              <XCircle size={14} /> Reject
            </button>
          )}

          {/* Post */}
          {!isNew && status === 'Approved' && canPost && (
            <button
              onClick={handlePost}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium"
            >
              <BookCheck size={14} /> Post to Ledger
            </button>
          )}

          {/* Reverse */}
          {!isNew && status === 'Posted' && canReverse && !journal?.reversalJournalId && (
            <button
              onClick={() => setModal('reverse')}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-amber-600 hover:bg-amber-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium"
            >
              <RotateCcw size={14} /> Reverse
            </button>
          )}

          {/* Cancel */}
          {!isNew && !['Posted', 'Cancelled'].includes(status) && canCancel && (
            <button
              onClick={() => setModal('cancel')}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-rose-600 hover:bg-rose-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium"
            >
              <Ban size={14} /> Cancel
            </button>
          )}

          {/* Print */}
          {!isNew && journal && (
            <button
              onClick={() => setIsPrintOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400
                         hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              title="Print journal voucher"
            >
              <Printer size={14} /> Print
            </button>
          )}

          {/* Status badges */}
          {!isNew && (
            <div className="ml-auto flex items-center gap-2">
              {journal?.isReversal && (
                <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-semibold rounded-full">
                  Reversal Entry
                </span>
              )}
              {/* Posting Status (distinct from workflow status) */}
              {status === 'Posted' && (
                <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-semibold rounded-full flex items-center gap-1">
                  <BookCheck size={10} /> Posted to Ledger
                </span>
              )}
              <span className={cn('px-3 py-1 rounded-full text-xs font-semibold', WORKFLOW_STATUS_COLORS[status])}>
                {WORKFLOW_STATUS_LABELS[status]}
              </span>
            </div>
          )}
        </div>

        {/* Alerts */}
        {error && (
          <div className="mt-2 flex items-start gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20
                          border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20
                          border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-400">
            <CheckCircle size={14} className="shrink-0" />
            <span>{success}</span>
          </div>
        )}
      </div>

      {/* ── Header form ──────────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">

        {/* Title row */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              {isNew ? 'New Journal Voucher' : `Journal ${journal?.documentNumber ?? ''}`}
            </h2>
            {!isNew && journal && (
              <p className="text-xs text-slate-400 mt-0.5">
                Created {new Date(journal.createdDate).toLocaleString()} by {journal.createdByName ?? '—'}
                {journal.modifiedDate && ` · Last modified ${new Date(journal.modifiedDate).toLocaleString()}`}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {!isNew && journal?.originalJournalId && (
              <button
                onClick={() => navigate(`/gl/journals/${journal.originalJournalId}`)}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                View Original Journal ↗
              </button>
            )}
            {!isNew && journal?.reversalJournalId && (
              <button
                onClick={() => navigate(`/gl/journals/${journal.reversalJournalId}`)}
                className="text-xs text-amber-600 dark:text-amber-400 hover:underline"
              >
                View Reversal ↗
              </button>
            )}
          </div>
        </div>

        {/* Fields grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">

          {/* Transaction Type */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              Transaction Type <span className="text-red-500">*</span>
            </label>
            {txTypes.length > 0 ? (
              <select
                value={transactionTypeCode}
                onChange={e => setTransactionTypeCode(e.target.value)}
                disabled={!isEditable || !isNew}
                className={fieldClass(!isEditable || !isNew)}
              >
                {txTypes.map(t => (
                  <option key={t.id} value={t.typeCode}>
                    {t.typeCode} — {t.description}
                  </option>
                ))}
                {!txTypes.some(t => t.typeCode === transactionTypeCode) && (
                  <option value={transactionTypeCode}>{transactionTypeCode}</option>
                )}
              </select>
            ) : (
              <input
                value={transactionTypeCode}
                onChange={e => setTransactionTypeCode(e.target.value.toUpperCase())}
                disabled={!isEditable || !isNew}
                placeholder="e.g. JV"
                className={fieldClass(!isEditable || !isNew)}
              />
            )}
          </div>

          {/* Document Date */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              Document Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={documentDate}
              onChange={e => setDocumentDate(e.target.value)}
              disabled={!isEditable}
              className={fieldClass(!isEditable)}
            />
          </div>

          {/* Posting Date */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              Posting Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={postingDate}
              onChange={e => setPostingDate(e.target.value)}
              disabled={!isEditable}
              className={fieldClass(!isEditable)}
            />
          </div>

          {/* Currency */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              Currency <span className="text-red-500">*</span>
            </label>
            {currencies.length > 0 ? (
              <select
                value={currencyCode}
                onChange={e => setCurrencyCode(e.target.value)}
                disabled={!isEditable}
                className={cn(fieldClass(!isEditable), 'font-mono')}
              >
                {currencies.map(c => (
                  <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                ))}
              </select>
            ) : (
              <input
                value={currencyCode}
                onChange={e => setCurrencyCode(e.target.value.toUpperCase())}
                disabled={!isEditable}
                maxLength={3}
                className={cn(fieldClass(!isEditable), 'font-mono uppercase')}
              />
            )}
          </div>

          {/* Exchange Rate */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              Exchange Rate
            </label>
            <input
              type="number"
              value={exchangeRate}
              onChange={e => setExchangeRate(parseFloat(e.target.value) || 1)}
              disabled={!isEditable}
              step="0.000001"
              min="0.000001"
              className={fieldClass(!isEditable)}
            />
          </div>

          {/* Reference */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              Reference
            </label>
            <input
              value={reference}
              onChange={e => setReference(e.target.value)}
              disabled={!isEditable}
              placeholder="Optional"
              className={fieldClass(!isEditable)}
            />
          </div>

          {/* Location — 3 cols */}
          <div className="col-span-2 md:col-span-3 lg:col-span-3">
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              Location
            </label>
            <select
              value={headerLocationId}
              onChange={e => handleLocationChange(e.target.value)}
              disabled={!isEditable}
              className={fieldClass(!isEditable)}
            >
              <option value="">— Select Location —</option>
              {accessibleLocations.map(l => (
                <option key={l.id} value={l.id}>{l.code} — {l.name}</option>
              ))}
            </select>
          </div>

          {/* Cost Center — 3 cols */}
          <div className="col-span-2 md:col-span-3 lg:col-span-3">
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              Cost Center
            </label>
            <select
              value={headerCostCenterId}
              onChange={e => setHeaderCostCenterId(e.target.value)}
              disabled={!isEditable}
              className={fieldClass(!isEditable)}
            >
              <option value="">— Select Cost Center —</option>
              {filteredCostCenters.map(cc => (
                <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>
              ))}
            </select>
          </div>

          {/* Narration — full width */}
          <div className="col-span-2 md:col-span-3 lg:col-span-6">
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              Narration
            </label>
            <input
              value={narration}
              onChange={e => setNarration(e.target.value)}
              disabled={!isEditable}
              placeholder="Describe the purpose of this journal entry…"
              className={fieldClass(!isEditable)}
            />
          </div>
        </div>
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6">
        <div className="flex">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={cn(
                'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
                tab === t.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300',
              )}
            >
              <t.icon size={14} />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab body ─────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto">

        {/* ── JOURNAL LINES ─────────────────────────────────────────────────── */}
        {tab === 'lines' && (
          <div className="flex flex-col h-full min-h-0">

            {/* Line grid */}
            <div className="flex-1 overflow-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-800/90 backdrop-blur-sm
                                  border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide w-9">#</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide min-w-[220px]">Account</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide min-w-[140px]">Narration</th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide w-36">Debit</th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide w-36">Credit</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide w-24">Currency</th>
                    {isEditable && <th className="w-9" />}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {lines.map((line, idx) => (
                    <tr key={line.key}
                        // F9 anywhere on the line (amount, narration…) opens this line's account ledger (422175)
                        onKeyDownCapture={ledgerShortcut(line.accountId)}
                        className="group hover:bg-blue-50/40 dark:hover:bg-slate-700/30 transition-colors">
                      {/* Line # */}
                      <td className="px-3 py-2 text-xs text-slate-400 select-none">{idx + 1}</td>

                      {/* Account */}
                      <td className="px-3 py-1.5">
                        <AccountCell
                          accountId={line.accountId}
                          locationId={line.locationId || headerLocationId || null}
                          accountCode={line.accountCode}
                          accountName={line.accountName}
                          disabled={!isEditable}
                          onChange={(id, code, name) =>
                            updateLine(line.key, { accountId: id, accountCode: code, accountName: name })
                          }
                        />
                      </td>

                      {/* Narration */}
                      <td className="px-3 py-1.5">
                        <input
                          value={line.narration}
                          onChange={e => updateLine(line.key, { narration: e.target.value })}
                          disabled={!isEditable}
                          placeholder="Line note…"
                          className="w-full px-2 py-1 text-sm rounded border border-transparent
                                     hover:border-slate-200 focus:border-blue-400 focus:bg-white
                                     bg-transparent disabled:bg-transparent outline-none
                                     text-slate-700 dark:text-slate-300 placeholder-slate-300"
                        />
                      </td>

                      {/* Debit */}
                      <td className="px-3 py-1.5">
                        <input
                          type="number"
                          value={line.debitAmount || ''}
                          onChange={e => {
                            const v = parseFloat(e.target.value) || 0;
                            updateLine(line.key, { debitAmount: v, creditAmount: v > 0 ? 0 : line.creditAmount });
                          }}
                          disabled={!isEditable || line.creditAmount > 0}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                          className={cn(
                            'w-full px-2 py-1 text-sm text-right rounded border border-transparent',
                            'hover:border-slate-200 focus:border-blue-400 focus:bg-white',
                            'bg-transparent disabled:bg-transparent outline-none font-mono',
                            'text-slate-900 dark:text-white placeholder-slate-300',
                            line.debitAmount > 0 && 'text-blue-700 dark:text-blue-400 font-semibold',
                          )}
                        />
                      </td>

                      {/* Credit */}
                      <td className="px-3 py-1.5">
                        <input
                          type="number"
                          value={line.creditAmount || ''}
                          onChange={e => {
                            const v = parseFloat(e.target.value) || 0;
                            updateLine(line.key, { creditAmount: v, debitAmount: v > 0 ? 0 : line.debitAmount });
                          }}
                          disabled={!isEditable || line.debitAmount > 0}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                          className={cn(
                            'w-full px-2 py-1 text-sm text-right rounded border border-transparent',
                            'hover:border-slate-200 focus:border-blue-400 focus:bg-white',
                            'bg-transparent disabled:bg-transparent outline-none font-mono',
                            'text-slate-900 dark:text-white placeholder-slate-300',
                            line.creditAmount > 0 && 'text-emerald-700 dark:text-emerald-400 font-semibold',
                          )}
                        />
                      </td>

                      {/* Currency */}
                      <td className="px-3 py-1.5 text-xs font-mono text-slate-500 dark:text-gray-400">
                        {currencies.length > 0 && isEditable ? (
                          <select
                            value={line.currencyCode || currencyCode}
                            onChange={e => updateLine(line.key, { currencyCode: e.target.value })}
                            className="w-full bg-transparent border-none outline-none text-xs font-mono
                                       text-slate-600 dark:text-slate-400 cursor-pointer"
                          >
                            {currencies.map(c => (
                              <option key={c.code} value={c.code}>{c.code}</option>
                            ))}
                          </select>
                        ) : (
                          <span>{line.currencyCode || currencyCode}</span>
                        )}
                      </td>

                      {/* Delete */}
                      {isEditable && (
                        <td className="px-2 py-1.5">
                          <button
                            onClick={() => removeLine(line.key)}
                            disabled={lines.length <= 2}
                            className="p-1 rounded text-slate-200 hover:text-red-500 hover:bg-red-50
                                       disabled:opacity-0 disabled:cursor-not-allowed
                                       opacity-0 group-hover:opacity-100 transition-all"
                            title="Remove line"
                          >
                            <Trash2 size={13} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer: add line + totals */}
            <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
              {isEditable && (
                <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-700">
                  <button
                    onClick={addLine}
                    className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700
                               dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-colors"
                  >
                    <Plus size={14} /> Add Line
                  </button>
                </div>
              )}

              {/* Totals */}
              <div className="flex items-center justify-end gap-8 px-6 py-3
                              bg-slate-50 dark:bg-slate-800/80">
                <div className="text-right">
                  <p className="text-xs text-slate-500 dark:text-gray-400 mb-0.5">Total Debit</p>
                  <p className="text-sm font-bold font-mono text-blue-700 dark:text-blue-400">
                    {totalDebit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500 dark:text-gray-400 mb-0.5">Total Credit</p>
                  <p className="text-sm font-bold font-mono text-emerald-700 dark:text-emerald-400">
                    {totalCredit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500 dark:text-gray-400 mb-0.5">Difference</p>
                  <p className={cn(
                    'text-sm font-bold font-mono flex items-center gap-1 justify-end',
                    isBalanced ? 'text-emerald-600 dark:text-emerald-400' : totalDebit === 0 ? 'text-slate-400' : 'text-red-600 dark:text-red-400',
                  )}>
                    {diff.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    {isBalanced && <CheckCircle size={14} className="text-emerald-500" />}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── WORKFLOW ─────────────────────────────────────────────────────── */}
        {tab === 'workflow' && (
          <div className="p-6 space-y-6">
            {/* Live workflow instance status */}
            {!isNew && journal && (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4">
                  Approval Status
                </h3>
                <WorkflowStatusPanel
                  entityName="GlJournal"
                  entityId={journal.id}
                />
              </div>
            )}
            {/* Historical approval timeline */}
            {(journal?.workflowHistory ?? []).length > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4">
                  Approval Timeline
                </h3>
                <ApprovalTimeline history={journal?.workflowHistory ?? []} />
              </div>
            )}
            {isNew && (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <History size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the journal first to track workflow.</p>
              </div>
            )}
          </div>
        )}

        {/* ── ATTACHMENTS ──────────────────────────────────────────────────── */}
        {tab === 'attachments' && (
          <div className="p-6">
            {!isNew && journal ? (
              <AttachmentPanel
                entityName="GlJournal"
                entityId={journal.id}
                canAdd={canAddDoc}
                canDelete={canDeleteDoc}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <Paperclip size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the journal first to add attachments.</p>
              </div>
            )}
          </div>
        )}

        {/* ── AUDIT LOG ────────────────────────────────────────────────────── */}
        {tab === 'audit' && (
          <div className="p-6">
            {!isNew && journal ? (
              <AuditViewer
                entityName="GlJournal"
                entityId={journal.id}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <ShieldCheck size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the journal first to view audit history.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Action Modals ─────────────────────────────────────────────────────── */}
      {modal === 'approve' && (
        <ActionModal
          title="Approve Journal"
          placeholder="Approval remarks (optional)…"
          required={false}
          onConfirm={text => { setModal(null); handleApprove(text); }}
          onCancel={() => setModal(null)}
        />
      )}
      {modal === 'reject' && (
        <ActionModal
          title="Reject Journal"
          placeholder="Rejection reason (required)…"
          required={true}
          onConfirm={text => { setModal(null); handleReject(text); }}
          onCancel={() => setModal(null)}
        />
      )}
      {modal === 'reverse' && (
        <ActionModal
          title="Create Reversal Journal"
          placeholder="Reversal narration (optional)…"
          required={false}
          onConfirm={text => { setModal(null); handleReverse(text); }}
          onCancel={() => setModal(null)}
        />
      )}
      {modal === 'cancel' && (
        <ActionModal
          title="Cancel Journal"
          placeholder="Cancellation reason (required)…"
          required={true}
          onConfirm={text => { setModal(null); handleCancel(text); }}
          onCancel={() => setModal(null)}
        />
      )}

      {/* ── Print Voucher ─────────────────────────────────────────────────────── */}
      {journal && (
        <MasterReportPreview
          isOpen={isPrintOpen}
          onClose={() => setIsPrintOpen(false)}
          title={`${journal.transactionTypeCode} Voucher — ${journal.documentNumber}`}
          fileBaseName={`GL-Journal-${journal.documentNumber}`}
          onDownloadExcel={() => exportToExcel({
            sheetName: 'Journal',
            title:    `${journal.transactionTypeCode} Voucher — ${journal.documentNumber}`,
            subtitle: `Date: ${journal.documentDate?.slice(0, 10) ?? ''}   Currency: ${journal.currencyCode ?? ''}`,
            columns: [
              { header: '#',            key: 'lineNumber',   width: 6,  align: 'center' },
              { header: 'Account Code', key: 'accountCode',  width: 16 },
              { header: 'Account Name', key: 'accountName',  width: 34 },
              { header: 'Narration',    key: 'narration',    width: 30 },
              { header: 'Debit',        key: 'debitAmount',  width: 16, numFmt: '#,##0.00', align: 'right' },
              { header: 'Credit',       key: 'creditAmount', width: 16, numFmt: '#,##0.00', align: 'right' },
            ],
            rows: (journal.lines ?? []).map(l => ({
              lineNumber:   l.lineNumber,
              accountCode:  l.accountCode ?? '',
              accountName:  l.accountName ?? '',
              narration:    l.narration ?? '',
              debitAmount:  l.debitAmount  || '',
              creditAmount: l.creditAmount || '',
            })),
            totalsRow: { accountName: 'TOTAL', debitAmount: journal.totalDebit ?? 0, creditAmount: journal.totalCredit ?? 0 },
          }, `GL-Journal-${journal.documentNumber}`)}
          email={{ documentType: 'GL Journal', documentNo: journal.documentNumber }}
        >
          <GlJournalVoucher
            journal={journal}
            companyName={authUser?.fullName ?? 'Demo Company'}
            printedBy={authUser?.fullName ?? authUser?.username ?? undefined}
          />
        </MasterReportPreview>
      )}
    </div>
  );
};
